import * as React from "react";

import { isConnected, TenantIdentity } from "Components/Tenants";
import { Badge, Tooltip } from "Components/ui";
import { Plug } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatRelative, pluralize } from "utils/format";
import { DashboardTenantRow } from "../data";
import { formatMemory, formatPct, TENANT_STATUS } from "../format";

type StatTone = "danger" | "warning";

// "3 d", "5 h" or "12 m": the process uptime in the tile's narrow pm2 column.
const shortDuration = (seconds: number): string => {
  const minutes = Math.max(1, Math.floor(seconds / 60));
  if (minutes >= 24 * 60) {
    return `${Math.floor(minutes / (24 * 60))} d`;
  }
  return minutes >= 60 ? `${Math.floor(minutes / 60)} h` : `${minutes} m`;
};

interface StatProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: StatTone;
}

// The tile's figures: label, value, one line of context. Values align across tiles in the grid.
const Stat: React.FC<StatProps & { quiet: boolean }> = ({ label, value, sub, tone, quiet }) => (
  <div className="min-w-0">
    <dt className="truncate text-xs text-muted-foreground">{label}</dt>
    <dd
      className={cn(
        "mt-1 truncate text-sm font-medium tabular-nums",
        tone === "danger"
          ? "text-danger-text"
          : tone === "warning"
          ? "text-warning-text"
          : quiet
          ? "text-muted-foreground"
          : "text-foreground"
      )}
    >
      {value}
    </dd>
    {sub && (
      <dd className="mt-0.5 truncate text-xs text-muted-foreground" title={sub}>
        {sub}
      </dd>
    )}
  </div>
);

const responseStat = ({ probe, history, status, process }: DashboardTenantRow): StatProps => {
  const label = "Response";
  if (!probe) {
    return { label, value: "—", sub: "Not checked yet" };
  }
  if (!probe.ok) {
    return {
      label,
      value: probe.httpStatus ? `HTTP ${probe.httpStatus}` : "No answer",
      tone: "danger",
    };
  }
  // Degraded with a healthy process means the portal itself is slow.
  const slow = status === "degraded" && (!process || process.status === "online");
  return {
    label,
    value: typeof probe.ms === "number" ? `${probe.ms} ms` : "—",
    tone: slow ? "warning" : undefined,
    sub: history && typeof history.p95Ms === "number" ? `p95 ${history.p95Ms} ms` : undefined,
  };
};

const uptimeStat = ({ history }: DashboardTenantRow): StatProps => {
  const label = "Uptime, 24 h";
  if (!history || typeof history.uptimePct !== "number") {
    return { label, value: "—", sub: "Collecting" };
  }
  return {
    label,
    value: formatPct(history.uptimePct, 1),
    sub: pluralize(history.samples, "check"),
  };
};

const processStat = ({
  process,
  processSource,
  deployment,
  slug,
}: DashboardTenantRow): StatProps => {
  const label = "pm2";
  if (processSource === "remote") {
    return { label, value: "—", sub: "Runs on another server" };
  }
  if (processSource === "unmapped") {
    return {
      label,
      value: "Not found",
      tone: "warning",
      sub: `No app "${deployment?.app || slug}"`,
    };
  }
  if (!process) {
    return { label, value: "—", sub: "pm2 unavailable" };
  }
  if (process.status !== "online") {
    return {
      label,
      value: `${process.status.charAt(0).toUpperCase()}${process.status.slice(1)}`,
      tone: "danger",
    };
  }
  const uptime = process.uptimeSec !== null ? ` · up ${shortDuration(process.uptimeSec)}` : "";
  return {
    label,
    value: formatMemory(process.memMb),
    sub: `${formatPct(process.cpuPct, 1)} CPU${uptime}`,
  };
};

/**
 * One tenant on the board. Tenants whose TeleOpd isn't connected are greyed out with muted text
 * and a flat tile (color, not opacity, so they stay readable) and are never clickable. A down or
 * degraded tenant keeps its colored edge and its reason, so it still stands out.
 */
const TenantTile: React.FC<{ tenant: DashboardTenantRow }> = ({ tenant }) => {
  const connected = isConnected(tenant.link);
  const quiet = !connected;
  const edge =
    tenant.status === "down"
      ? "border-danger/50"
      : tenant.status === "degraded"
      ? "border-warning/50"
      : "border-border";
  const status = TENANT_STATUS[tenant.status];

  return (
    <li
      className={cn(
        "flex min-w-0 flex-col gap-4 rounded-card border p-5",
        edge,
        connected ? "bg-surface shadow-xs" : "bg-surface/50"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <TenantIdentity
          name={tenant.name}
          slug={tenant.slug}
          uri={tenant.uri}
          className={cn("text-sm", quiet && "text-muted-foreground")}
        />
        {tenant.status === "up" && (
          <Tooltip content="Up: the health check answers">
            <span className="flex h-5 shrink-0 items-center">
              <span className="h-2.5 w-2.5 rounded-full bg-success" />
              <span className="sr-only">Up</span>
            </span>
          </Tooltip>
        )}
      </div>

      {tenant.status !== "up" && (
        <div className="-mt-1 flex items-start gap-2">
          <Badge tone={status.tone} dot className="shrink-0">
            {status.label}
          </Badge>
          {tenant.statusReason && (
            <p className="pt-0.5 text-xs text-muted-foreground">{tenant.statusReason}</p>
          )}
        </div>
      )}

      <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)] gap-x-4 border-t border-border pt-4">
        {[responseStat(tenant), uptimeStat(tenant), processStat(tenant)].map((stat) => (
          <Stat key={stat.label} {...stat} quiet={quiet} />
        ))}
      </dl>

      {connected && (
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-primary-text">
          <Plug aria-hidden="true" className="h-3.5 w-3.5" />
          {tenant.link.lastSyncAt
            ? `Synced ${formatRelative(tenant.link.lastSyncAt)}`
            : "Never synced"}
          {tenant.link.appVersion && (
            <span className="text-muted-foreground">· TeleOpd v{tenant.link.appVersion}</span>
          )}
        </p>
      )}
    </li>
  );
};

export default TenantTile;
